import { isNotDefined } from '@sgme/fp';
import type {
  CpOrderGetOrderResponse,
  LinkedToClientResponse,
  PreviousReloadFormResponse,
  ReloadFormRequest,
} from 'apiConnector/secondary-market/cp-order/model';
import { isDominoGetSettlementTypesReport } from 'apiConnector/secondary-market/reports';
import type { SettlementTypesResponse } from 'apiConnector/secondary-market/shared/settlement';
import type { Custodian } from 'models/custodian';
import type { ISalesCounterpart } from 'models/ISalesCounterpart';
import { secondaryMarketCpOrderPlacementClose } from 'state/secondaryMarket/cpOrder/order';
import { findDefaultClient } from 'state/secondaryMarket/referential/bcsProduct/bcsProduct.utils';
import type { TradeWay } from 'state/secondaryMarket/secondaryMarket.model';
import { fromSecondaryMarket } from 'state/selectors';
import { getBcsProductKeyByWorkspace } from 'state/selectors/common/tabs';
import type { Thunk, ThunkDispatch } from 'state/state.common';
import { AppState } from 'state/state.model';
import type { WorkspaceType } from 'state/workspaces/model';
import { Report } from 'utils/reports';

import type { ClientType } from '../../shared/form.models';
import { getBcsProductKeyFormCpOrderTab } from '../cpOrder.selectors';
import { fromSettlementTypeKey, getCobDate } from '../cpOrder.utils';
import {
  secondaryMarketCpFormLinkedToClientReceived,
  secondaryMarketCpFormLinkedToClientRequested,
  secondaryMarketCpFormSettlementTypesRequested,
  secondaryMarketCpOrderSettlementTypeReferentialReset,
} from '../referential/client/client.actions';
import type { ClientData } from '../referential/client/client.model';
import { cpOrderKycActions } from '../referential/kyc/kyc.actions';
import { secondaryMarketCpOrderKycStatusRequestedThunk } from '../referential/kyc/kyc.thunks';
import {
  secondaryMarketCpOrderInitFormFetched,
  secondaryMarketCpOrderReloadFormFetched,
  secondaryMarketCpOrderReloadFormFetchRequested,
} from '../referential/product/product.actions';
import { secondaryMarketDominoSettlementDateRequested } from '../referential/referential.actions';
import {
  getFormClientBdrId,
  getFormClientEliotCode,
  getFormClientType,
  getFormCustodianBdrId,
  getFormSalesId,
  getFormSettlementDate,
  getFormTradeWay,
  getFormValuationDate,
  getTradeDateType,
} from './cpOrderForm.selectors';
import { secondaryMarketCpOrderFormFieldChanged } from './input/cpOrderInputForm.actions';
import type { TradeDateType } from './input/cpOrderInputForm.model';
import { getInputFormClient, getInputFormTradeSizeInputMode } from './input/cpOrderInputForm.selectors';
import { secondaryMarketAmendCpOrderFetched } from './original/cpOrderOriginalForm.actions';
import { getOriginalFormClient } from './original/cpOrderOriginalForm.selector';
import { isSameClient } from './utils';

function getStatus(state: AppState, tabId: string) {
  const getCpOrderValidationErrors = fromSecondaryMarket.cpOrder.form.makeGetCpOrderValidationErrors();

  const getCpOrderStatus = fromSecondaryMarket.cpOrder.order.makeGetCpOrderStatus(getCpOrderValidationErrors);
  const status = getCpOrderStatus(state, tabId);

  return {
    shouldResetSidePanel: status === 'ORDER_FAILED',
  };
}

function resetSidePanelIfNeeded(dispatch: ThunkDispatch, getState: () => AppState, tabId: string) {
  const state = getState();
  const { shouldResetSidePanel } = getStatus(state, tabId);
  if (shouldResetSidePanel) {
    dispatch(secondaryMarketCpOrderPlacementClose(tabId));
  }
}

export function defaultQuantityThunk(tabId: string, cashAmount: number): Thunk<void> {
  return (dispatch, getState) => {
    const state = getState();

    const perUnit = fromSecondaryMarket.cpOrder.getCashAmountPerQuantityUnit(state, tabId);
    if (perUnit !== undefined) {
      const defaultQuantity = Math.round(cashAmount / perUnit);
      dispatch(secondaryMarketCpOrderFormFieldChanged(tabId, 'quantity', defaultQuantity));
    }
  };
}

export function secondaryMarketCpOrderCashAmountChangedThunk(tabId: string, cashAmount?: number): Thunk<void> {
  return (dispatch, getState) => {
    resetSidePanelIfNeeded(dispatch, getState, tabId);

    if (cashAmount === undefined) {
      dispatch(secondaryMarketCpOrderFormFieldChanged(tabId, 'cashAmount', undefined));
      dispatch(secondaryMarketCpOrderFormFieldChanged(tabId, 'quantity', undefined));
      return;
    }
    dispatch(defaultQuantityThunk(tabId, cashAmount));
    dispatch(secondaryMarketCpOrderFormFieldChanged(tabId, 'cashAmount', cashAmount));
    dispatch(secondaryMarketCpOrderFormFieldChanged(tabId, 'tradeSizeInputMode', 'amount'));
  };
}

export function defaultCashAmountThunk(tabId: string, quantity: number): Thunk<void> {
  return (dispatch, getState) => {
    const state = getState();
    const perUnit = fromSecondaryMarket.cpOrder.getCashAmountPerQuantityUnit(state, tabId);
    if (perUnit !== undefined) {
      const defaultCashAmount = perUnit * quantity;
      dispatch(secondaryMarketCpOrderFormFieldChanged(tabId, 'cashAmount', defaultCashAmount));
    }
  };
}

export function secondaryMarketCpOrderQuantityChangedThunk(tabId: string, quantity?: number): Thunk<void> {
  return (dispatch, getState) => {
    resetSidePanelIfNeeded(dispatch, getState, tabId);
    if (quantity === undefined) {
      dispatch(secondaryMarketCpOrderFormFieldChanged(tabId, 'cashAmount', undefined));
      dispatch(secondaryMarketCpOrderFormFieldChanged(tabId, 'quantity', undefined));
      return;
    }
    dispatch(defaultCashAmountThunk(tabId, quantity));
    dispatch(secondaryMarketCpOrderFormFieldChanged(tabId, 'quantity', quantity));
    dispatch(secondaryMarketCpOrderFormFieldChanged(tabId, 'tradeSizeInputMode', 'quantity'));
  };
}

export function secondaryMarketCpOrderTradingPriceChangedThunk(tabId: string, value?: number): Thunk<void> {
  return (dispatch, getState) => {
    resetSidePanelIfNeeded(dispatch, getState, tabId);
    dispatch(secondaryMarketCpOrderFormFieldChanged(tabId, 'tradingPrice', value));
  };
}

export function secondaryMarketCpOrderSalesMarginChangedThunk(tabId: string, value?: number): Thunk<void> {
  return (dispatch, getState) => {
    resetSidePanelIfNeeded(dispatch, getState, tabId);
    dispatch(secondaryMarketCpOrderFormFieldChanged(tabId, 'salesMargin', value));
  };
}

export function secondaryMarketCpOrderSettlementDateChangedThunk(tabId: string, value: Nullable<string>): Thunk<void> {
  return (dispatch, getState) => {
    resetSidePanelIfNeeded(dispatch, getState, tabId);
    if (value !== null) {
      dispatch(secondaryMarketCpOrderFormFieldChanged(tabId, 'settlementDate', value));
    }
  };
}

export function secondaryMarketCpOrderTradeWayChangedThunk(tabId: string, value: TradeWay): Thunk<void> {
  return (dispatch, getState) => {
    const state = getState();
    resetSidePanelIfNeeded(dispatch, getState, tabId);

    dispatch(secondaryMarketCpOrderFormFieldChanged(tabId, 'tradeWay', value));
    const mode = fromSecondaryMarket.cpOrder.formInput.getInputFormTradeSizeInputMode(state, tabId);
    const quantity = fromSecondaryMarket.cpOrder.form.getFormQuantity(state, tabId);
    const cashAmount = fromSecondaryMarket.cpOrder.form.getFormCashAmount(state, tabId);
    if (mode === 'amount' && cashAmount !== undefined) {
      dispatch(defaultQuantityThunk(tabId, cashAmount));
    }
    if (mode === 'quantity' && quantity !== undefined) {
      dispatch(defaultCashAmountThunk(tabId, quantity));
    }
  };
}

export function secondaryMarketCpOrderCashAmountResetThunk(tabId: string): Thunk<void> {
  return (dispatch, getState) => {
    resetSidePanelIfNeeded(dispatch, getState, tabId);
    dispatch(secondaryMarketCpOrderFormFieldChanged(tabId, 'tradeSizeInputMode', 'amount'));
  };
}

export function secondaryMarketCpOrderQuantityResetThunk(tabId: string): Thunk<void> {
  return (dispatch, getState) => {
    resetSidePanelIfNeeded(dispatch, getState, tabId);
    dispatch(secondaryMarketCpOrderFormFieldChanged(tabId, 'tradeSizeInputMode', 'quantity'));
  };
}

function resetClientData(dispatch: ThunkDispatch, tabId: string) {
  dispatch(secondaryMarketCpOrderFormFieldChanged(tabId, 'salesId', undefined));
  dispatch(secondaryMarketCpOrderFormFieldChanged(tabId, 'settlementType', undefined));
}

export function secondaryMarketCpOrderClientSelectedThunk(
  tabId: string,
  workspace: WorkspaceType,
  client: ISalesCounterpart | undefined,
): Thunk<void> {
  return (dispatch, getState) => {
    resetSidePanelIfNeeded(dispatch, getState, tabId);
    dispatch(secondaryMarketCpOrderFormFieldChanged(tabId, 'client', client));
    resetClientData(dispatch, tabId);
    dispatch(secondaryMarketCpOrderSettlementTypeReferentialReset(tabId));
    dispatch(cpOrderKycActions.secondaryMarketKycStatusReset(tabId));

    if (client) {
      dispatch(secondaryMarketCpFormLinkedToClientRequested(tabId));
    }
    dispatch(secondaryMarketDominoSettlementDateRequestedThunk(tabId, workspace));
  };
}

export function secondaryMarketCpOrderClientTypeSelectedThunk(
  tabId: string,
  workspace: WorkspaceType,
  clientType: ClientType,
): Thunk<void> {
  return (dispatch, getState) => {
    const state = getState();
    resetSidePanelIfNeeded(dispatch, getState, tabId);

    const bcsProductKey = getBcsProductKeyFormCpOrderTab(state, workspace, tabId);
    if (bcsProductKey !== undefined) {
      dispatch(secondaryMarketCpOrderFormFieldChanged(tabId, 'clientType', clientType));
      resetClientData(dispatch, tabId);
      dispatch(secondaryMarketCpOrderSettlementTypeReferentialReset(tabId));
      dispatch(cpOrderKycActions.secondaryMarketKycStatusReset(tabId));

      if (clientType === 'Other') {
        dispatch(secondaryMarketCpOrderResetToDefaultClientThunk(tabId, workspace));
      } else {
        dispatch(secondaryMarketCpFormLinkedToClientRequested(tabId));
      }
      dispatch(secondaryMarketDominoSettlementDateRequestedThunk(tabId, workspace));
    }
  };
}

export function secondaryMarketCpOrderCustodianSelectedThunk(
  tabId: string,
  workspace: WorkspaceType,
  custodian: Custodian | undefined | null,
): Thunk<void> {
  return (dispatch, getState) => {
    const state = getState();
    resetSidePanelIfNeeded(dispatch, getState, tabId);

    const bcsProductKey = getBcsProductKeyByWorkspace(state, workspace, tabId);
    if (bcsProductKey !== undefined) {
      dispatch(secondaryMarketCpOrderFormFieldChanged(tabId, 'custodian', custodian ?? undefined));
      dispatch(secondaryMarketCpOrderFormFieldChanged(tabId, 'settlementType', undefined));
      dispatch(secondaryMarketCpOrderSettlementTypeReferentialReset(tabId));
      dispatch(cpOrderKycActions.secondaryMarketKycStatusReset(tabId));
      const client = fromSecondaryMarket.cpOrder.form.getFormClient(state, tabId);
      if (custodian?.id !== undefined && client?.id !== undefined) {
        dispatch(secondaryMarketCpFormSettlementTypesRequested(tabId, bcsProductKey, client, custodian.id));
      }
    }
  };
}

export function secondaryMarketCpOrderResetToDefaultClientThunk(tabId: string, workspace: WorkspaceType): Thunk<void> {
  return (dispatch, getState) => {
    const state = getState();
    const clients = fromSecondaryMarket.cpOrder.referential.product.getCpOrderBcsProductClients(state, tabId);
    const defaultClient = findDefaultClient(clients);
    dispatch(secondaryMarketCpOrderClientSelectedThunk(tabId, workspace, defaultClient));
  };
}

export function secondaryMarketCpOrderSelectSalesDeskThunk(tabId: string, salesDesk: string | undefined): Thunk<void> {
  return (dispatch, getState) => {
    const state = getState();
    const salesId = fromSecondaryMarket.cpOrder.referential.client.getSelectedSaleIdFromDesk(state, tabId, salesDesk);

    resetSidePanelIfNeeded(dispatch, getState, tabId);
    dispatch(secondaryMarketCpOrderFormFieldChanged(tabId, 'salesId', salesId));
    dispatch(secondaryMarketCpOrderFormFieldChanged(tabId, 'salesDesk', salesDesk));
    if (salesId !== undefined) {
      dispatch(secondaryMarketCpOrderKycStatusRequestedThunk(tabId));
    } else {
      dispatch(cpOrderKycActions.secondaryMarketKycStatusReset(tabId));
    }
  };
}
export const secondaryMarketCpOrderTradeSizeInputModeChangedThunk = (tabId: string): Thunk<void> => {
  return (dispatch, getState) => {
    const state = getState();
    const mode = getInputFormTradeSizeInputMode(state, tabId);
    resetSidePanelIfNeeded(dispatch, getState, tabId);
    dispatch(
      secondaryMarketCpOrderFormFieldChanged(tabId, 'tradeSizeInputMode', mode === 'quantity' ? 'amount' : 'quantity'),
    );
  };
};
export function secondaryMarketCpOrderSelectSalesIdThunk(tabId: string, salesId: string | undefined): Thunk<void> {
  return (dispatch, getState) => {
    resetSidePanelIfNeeded(dispatch, getState, tabId);
    dispatch(secondaryMarketCpOrderFormFieldChanged(tabId, 'salesId', salesId));
    dispatch(secondaryMarketCpOrderFormFieldChanged(tabId, 'salesDesk', undefined));
    dispatch(secondaryMarketCpOrderKycStatusRequestedThunk(tabId));
  };
}

export function secondaryMarketCpOrderFormValuationDateChangedThunk(
  tabId: string,
  workspace: WorkspaceType,
  valuationDate?: Nullable<string>,
): Thunk<void> {
  return (dispatch, getState) => {
    resetSidePanelIfNeeded(dispatch, getState, tabId);
    if (valuationDate !== null) {
      dispatch(secondaryMarketCpOrderFormFieldChanged(tabId, 'valuationDate', valuationDate));
      dispatch(secondaryMarketDominoSettlementDateRequestedThunk(tabId, workspace));
    }
  };
}

export function secondaryMarketCpOrderSettlementTypeChangedThunk(tabId: string, value?: string): Thunk<void> {
  return (dispatch, getState) => {
    resetSidePanelIfNeeded(dispatch, getState, tabId);
    dispatch(secondaryMarketCpOrderFormFieldChanged(tabId, 'settlementType', fromSettlementTypeKey(value)));
  };
}

function secondaryMarketDominoSettlementDateRequestedThunk(tabId: string, workspace: WorkspaceType): Thunk<void> {
  return (dispatch, getState) => {
    const state = getState();
    const bcsProductKey = getBcsProductKeyByWorkspace(state, workspace, tabId);
    const valuationDate = getFormValuationDate(state, tabId);
    const cobDate = getCobDate(valuationDate);
    const tradeDateType = getTradeDateType(state, tabId);
    const counterpartyEliotId = getFormClientEliotCode(state, tabId);
    const clientType = getFormClientType(state, tabId);

    if (valuationDate !== undefined && bcsProductKey !== undefined) {
      dispatch(
        secondaryMarketDominoSettlementDateRequested(
          tabId,
          bcsProductKey,
          valuationDate,
          cobDate,
          tradeDateType,
          counterpartyEliotId,
          clientType,
        ),
      );
    }
  };
}

export function secondaryMarketCpOrderFormTradeDateTypeChangedThunk(
  tabId: string,
  workspace: WorkspaceType,
  tradeDateType: TradeDateType,
): Thunk<void> {
  return (dispatch, getState) => {
    const state = getState();
    const bcsProductKey = getBcsProductKeyByWorkspace(state, workspace, tabId);
    const valuationDate = fromSecondaryMarket.cpOrder.form.getFormValuationDate(state, tabId);
    const cobDate = getCobDate(valuationDate);
    const counterpartyEliotId = getFormClientEliotCode(state, tabId);
    const clientType = getFormClientType(state, tabId);

    resetSidePanelIfNeeded(dispatch, getState, tabId);
    dispatch(secondaryMarketCpOrderFormFieldChanged(tabId, 'tradeDateType', tradeDateType));

    if (valuationDate !== undefined && bcsProductKey !== undefined) {
      dispatch(
        secondaryMarketDominoSettlementDateRequested(
          tabId,
          bcsProductKey,
          valuationDate,
          cobDate,
          tradeDateType,
          counterpartyEliotId,
          clientType,
        ),
      );
    }
  };
}

export function secondaryMarketCpOrderInitFormFetchedThunk(
  tabId: string,
  response: PreviousReloadFormResponse,
): Thunk<void> {
  return dispatch => {
    dispatch(secondaryMarketCpOrderInitFormFetched(tabId, response));

    dispatch(
      secondaryMarketCpOrderClientInteractionFetchedThunk(tabId, response.clientInteractionResponse, response.errors),
    );
    dispatch(secondaryMarketCpOrderFormFieldChanged(tabId, 'settlementDate', response.selectedSettlementDate));
    dispatch(secondaryMarketCpOrderFormFieldChanged(tabId, 'firstSettlementDate', response.firstSettlementDate));
    dispatch(secondaryMarketCpOrderFormFieldChanged(tabId, 'valuationDate', response.selectedValuationDate));
    dispatch(secondaryMarketCpOrderFormFieldChanged(tabId, 'tradeWay', response.selectedTradeWay));

    if (response.selectedCustodian) {
      dispatch(secondaryMarketCpOrderFormFieldChanged(tabId, 'custodian', response.selectedCustodian));
    }
  };
}

export function secondaryMarketCpOrderReloadFormFetchedThunk(
  tabId: string,
  workspace: WorkspaceType,
  response: PreviousReloadFormResponse,
): Thunk<void> {
  return dispatch => {
    dispatch(secondaryMarketCpOrderReloadFormFetched(tabId, workspace, response));
    dispatch(
      secondaryMarketCpOrderClientInteractionFetchedThunk(tabId, response.clientInteractionResponse, response.errors),
    );
  };
}

export function secondaryMarketCpOrderClientInteractionFetchedThunk(
  tabId: string,
  clientInteractionResponse: LinkedToClientResponse,
  errors?: Report[],
): Thunk<void> {
  return (dispatch, getState) => {
    if (isNotDefined(clientInteractionResponse)) {
      return;
    }
    const state = getState();
    const oldClientBdrId = getFormClientBdrId(state, tabId);
    const oldClientEliotCode = getFormClientEliotCode(state, tabId);
    const oldSalesId = getFormSalesId(state, tabId);
    dispatch(
      secondaryMarketCpFormLinkedToClientReceived(
        tabId,
        clientDataFromResponse(clientInteractionResponse),
        errors?.find(isDominoGetSettlementTypesReport),
      ),
    );
    if (!isSameClient(clientInteractionResponse.client!, oldClientBdrId, oldClientEliotCode)) {
      dispatch(secondaryMarketCpOrderFormFieldChanged(tabId, 'client', clientInteractionResponse.client));
    }
    if (
      clientInteractionResponse.selectedSale?.id !== undefined &&
      clientInteractionResponse.selectedSale?.id !== oldSalesId
    ) {
      dispatch(secondaryMarketCpOrderFormFieldChanged(tabId, 'salesId', clientInteractionResponse.selectedSale.id));
      dispatch(secondaryMarketCpOrderKycStatusRequestedThunk(tabId));
    }
  };
}

export function secondaryMarketCpOrderSettlementTypeFetchedThunk(
  tabId: string,
  response: SettlementTypesResponse,
): Thunk<void> {
  return dispatch => {
    dispatch(secondaryMarketCpFormLinkedToClientReceived(tabId, clientDataFromSettlementTypesResponse(response)));
  };
}

function clientDataFromResponse(response: LinkedToClientResponse): ClientData {
  return {
    allSales: response.sales,
    settlements: response.settlements,
    selectedSale: response.selectedSale,
    client: response.client,
  } as ClientData;
}

function clientDataFromSettlementTypesResponse(response: SettlementTypesResponse): ClientData {
  return <ClientData>{
    settlements: response.settlements,
  };
}

export function secondaryMarketAmendCpOrderFetchedThunk(
  tabId: string,
  orderId: string,
  response: CpOrderGetOrderResponse,
): Thunk<void> {
  return (dispatch, getState) => {
    dispatch(secondaryMarketAmendCpOrderFetched(tabId, orderId, response));
    const state = getState();
    const inputClient = getInputFormClient(state, tabId);
    const originalClient = getOriginalFormClient(state, tabId);
    const hasModifiedClient = inputClient !== undefined;
    const loadReferentialRequest = {
      bcsProductKey: response.bcsKey,
      clientType: getFormClientType(state, tabId),
      client: hasModifiedClient ? inputClient : originalClient,
      custodianBdrId: getFormCustodianBdrId(state, tabId),
      salesId: getFormSalesId(state, tabId),
      settlementDate: getFormSettlementDate(state, tabId),
      valuationDate: getFormValuationDate(state, tabId),
      tradeWay: getFormTradeWay(state, tabId),
    } as ReloadFormRequest;
    dispatch(
      secondaryMarketCpOrderReloadFormFetchRequested(tabId, 'secondaryMarketPostEvents', loadReferentialRequest),
    );
  };
}
